/**
 * ActiveHooks — W55 "Active Hook Campaigns"
 *
 * List: campaign name, channel + days running, MQLs attributed.
 * Shows outreach_hooks with status='approved' from last 30 days.
 *
 * Data: outreach_hooks joined to marketing_events for event name + date.
 * MQL attribution = proxy count of accounts in Suspect+ per event.
 * (No direct account_id on marketing_events — documented W55 gap.)
 *
 * Tenant 1 only.
 */

import { useEffect, useState } from "react";
import { supabase, getCurrentTenant } from "@/lib/session";

const TENANT_ID = 1;

const HOOK_TYPE_LABELS: Record<string, string> = {
  invite:        "LinkedIn",
  participation: "Email",
  round_table:   "Roundtable",
};

interface HookCampaign {
  id: string;
  name: string;
  hookType: string;
  channel: string;
  daysRunning: number;
  mqls: number; // proxy
  approvedAt: string | null;
}

export function ActiveHooks() {
  const [hooks, setHooks] = useState<HookCampaign[]>([]);
  const [loading, setLoading] = useState(true);
  const tenant = getCurrentTenant();

  useEffect(() => {
    const tenantId = tenant?.tenantId ?? TENANT_ID;
    let cancelled = false;
    setLoading(true);

    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    const startDate = thirtyDaysAgo.toISOString().slice(0, 10);

    supabase()
      .from("outreach_hooks")
      .select(`
        id,
        hook_type,
        status,
        approved_at,
        created_at,
        marketing_events!left(
          id,
          name,
          event_date,
          tenant_id
        )
      `)
      .eq("tenant_id", tenantId)
      .eq("status", "approved")
      .gte("approved_at", thirtyDaysAgo.toISOString())
      .order("approved_at", { ascending: false })
      .limit(10)
      .then(({ data }) => {
        if (cancelled) return;

        const now = new Date();
        const campaigns: HookCampaign[] = (data ?? []).map((h: any) => {
          const event = Array.isArray(h.marketing_events)
            ? h.marketing_events[0]
            : h.marketing_events;
          const name = event?.name ?? "Unnamed campaign";
          const approvedAt = h.approved_at ?? h.created_at ?? null;
          const daysRunning = approvedAt
            ? Math.max(0, Math.floor((now.getTime() - new Date(approvedAt).getTime()) / 86400000))
            : 0;

          return {
            id: h.id,
            name,
            hookType: h.hook_type ?? "invite",
            channel: HOOK_TYPE_LABELS[h.hook_type] ?? "Email",
            daysRunning,
            mqls: 0, // no direct attribution without account_id on events
            approvedAt,
          };
        });

        setHooks(campaigns);
        setLoading(false);
      })
      .catch(() => {
        if (!cancelled) {
          setHooks([]);
          setLoading(false);
        }
      });

    return () => { cancelled = true; };
  }, [tenant?.tenantId]);

  if (loading) {
    return (
      <div className="rounded-lg border border-neutral-200 bg-white p-4 animate-pulse">
        <div className="h-4 w-40 bg-neutral-100 rounded mb-4" />
        {[1,2,3].map(i => <div key={i} className="h-12 w-full bg-neutral-100 rounded mb-2" />)}
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-neutral-200 bg-white p-4">
      <div className="mb-1 text-[10px] font-bold uppercase tracking-widest text-neutral-500">
        Active Hook Campaigns
      </div>
      <p className="mb-4 text-[11px] text-neutral-400">
        Approved hooks active in the last 30 days.
      </p>

      {hooks.length === 0 ? (
        <div className="py-4 text-center text-sm text-neutral-400">
          No active hook campaigns.{" "}
          <a href="#/marketing/hooks" className="text-[#0B0B0F] hover:underline">
            Review and approve hooks
          </a>{" "}
          to activate campaigns.
        </div>
      ) : (
        <div className="divide-y divide-neutral-100">
          {hooks.map(h => (
            <div key={h.id} className="flex items-center gap-3 py-2.5">
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-neutral-900 truncate">{h.name}</div>
                <div className="text-[11px] text-neutral-400">
                  {h.channel} &middot; {h.daysRunning}d running
                </div>
              </div>
              <div className="text-right shrink-0">
                <div className="text-sm font-semibold tabular-nums text-neutral-700">
                  {h.mqls > 0 ? h.mqls : "—"}
                </div>
                <div className="text-[10px] text-neutral-400">MQLs</div>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="mt-3 text-right">
        <a href="#/marketing/hooks" className="text-[11px] text-neutral-400 hover:text-[#DC2626] hover:underline">
          Manage all hooks →
        </a>
      </div>
    </div>
  );
}
